"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import * as React from "react";
import { AncestorLayerContext } from "../../lib/backdrop-layer";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import { useVisualViewportBottomInset } from "../../hooks/use-visual-viewport-inset";
import { useTouchAwareInitialFocus } from "../../hooks/use-open-focus";
import { useIsMobile } from "../../hooks/use-media-query";
import { useEnterOnMount } from "../../hooks/use-enter-on-mount";
import { SURFACE_BG, type SurfaceToken } from "./box-classes";
import { Button } from "./button";
import {
  Drawer,
  DrawerClose,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTitleRow,
  type DrawerSize,
  type DrawerTitleRowPrimary,
} from "./drawer";
import { DialogHost } from "./dialog-host";
import { Icon, type IconName } from "./icon";
import { ScrollArea } from "./scroll-area";
import type { Omittable } from "../../lib/props";

// A responsive Dialog presents as a bottom sheet on a phone (the library
// Drawer: drag bar, swipe and tap-outside dismissal) and as a dialog
// everywhere else. The root decides once and every part below follows it
// through this context, so one tree serves both.
const DialogSheetContext = React.createContext(false);


/**
 * A dialog's actions, declared once on `DialogPopup` and built for the mode:
 * the dialog gets a footer (`start` · Cancel · primary); the sheet gets the
 * phone modal title row (`DrawerTitleRow`) — Cancel as the round outline ×
 * on the leading edge, the primary as the WORD in a round button on the
 * trailing edge — and `start` in a quiet footer.
 */
export type DialogOptions = Omittable<{
  primary: DrawerTitleRowPrimary;
  /** Default true: an outline "Cancel" that closes the dialog. Pass an
   *  object to relabel it, run your own close, or disable it mid-save.
   *  `false` drops the footer Cancel where the dialog has another door (a
   *  corner ×); the sheet keeps its × — it has no other door. */
  cancel?:
    | boolean
    | Omittable<{ label?: string; onClick?: () => void; disabled?: boolean }>;
  /** Footer-left content kept in both modes — a one-shot choice, a date. */
  start?: React.ReactNode;
}>;

const DialogOptionsContext = React.createContext<DialogOptions | null>(null);

function DialogOptionsFooter({ options }: { options: DialogOptions }): React.ReactElement {
  const { primary, cancel = true, start } = options;
  const cancelConfig = typeof cancel === "object" ? cancel : null;
  return (
    <DialogFooter>
      {start != null ? (
        <div className="flex min-w-0 flex-1 items-center gap-2">{start}</div>
      ) : null}
      {cancel === false ? null : cancelConfig?.onClick ? (
        <Button
          variant="outline"
          onClick={cancelConfig.onClick}
          disabled={cancelConfig.disabled}
        >
          {cancelConfig.label ?? "Cancel"}
        </Button>
      ) : (
        <DialogClose
          render={<Button variant="outline" disabled={cancelConfig?.disabled} />}
        >
          {cancelConfig?.label ?? "Cancel"}
        </DialogClose>
      )}
      <Button
        onClick={primary.onClick}
        type={primary.form ? "submit" : "button"}
        form={primary.form}
        disabled={primary.disabled}
        loading={primary.loading}
        {...(primary.destructive
          ? { color: "destructive" as const, variant: "outline" as const }
          : {})}
      >
        {primary.label}
      </Button>
    </DialogFooter>
  );
}

export type DialogProps = DialogPrimitive.Root.Props &
  Omittable<{
    /** Present as a bottom sheet on a phone. Default true; turn off for a
     *  dialog that is fullscreen media, desktop-only work, or that already
     *  renders its own phone presentation. */
    responsive?: boolean;
    /** Sheet only: the drawer's height — see `DrawerSize`. Default `half`;
     *  `full` for a body worked in (a list and its detail, a workspace). */
    sheetSize?: DrawerSize;
    /** Sheet only: the element to focus once the sheet has settled, so the
     *  software keyboard never opens mid-animation. A `half` sheet with one
     *  opens at its top stop, where the keyboard leaves the field in view. */
    sheetFocus?: React.RefObject<HTMLElement | null>;
  }>;

export function Dialog({
  responsive = true,
  sheetSize = "half",
  sheetFocus,
  onOpenChangeComplete,
  open: openProp,
  ...props
}: DialogProps): React.ReactElement {
  // A dialog mounted already open still enters — see useEnterOnMount.
  const open = useEnterOnMount(openProp);
  const isMobile = useIsMobile();
  const sheet = responsive && isMobile;
  const handleOpenChangeComplete = React.useCallback(
    (open: boolean) => {
      if (open && sheet) sheetFocus?.current?.focus();
      onOpenChangeComplete?.(open);
    },
    [sheet, sheetFocus, onOpenChangeComplete],
  );
  return (
    <DialogSheetContext.Provider value={sheet}>
      {sheet ? (
        <Drawer
          {...(props as React.ComponentProps<typeof Drawer>)}
          open={open}
          onOpenChangeComplete={handleOpenChangeComplete}
          size={sheetSize}
          opensAtTop={sheetFocus != null}
        />
      ) : (
        <DialogPrimitive.Root
          {...props}
          open={open}
          onOpenChangeComplete={handleOpenChangeComplete}
        />
      )}
    </DialogSheetContext.Provider>
  );
}

/**
 * A further action for a sheet's title row (`DialogHeader sheetActions`): a
 * round outline glyph on the row's one 32px rung, seated before the
 * primary. The commit itself is never a glyph — `dialogOptions.primary`
 * renders its word.
 */
export function DialogSheetAction({
  icon,
  label,
  onClick,
  disabled,
}: Omittable<{
  icon: IconName;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}>): React.ReactElement {
  return (
    <Button
      variant="outline"
      size="icon"
      shape="pill"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon name={icon} />
    </Button>
  );
}

// `overflow-clip` (not `hidden`): the radius clips every child fill — a table,
// a sidebar pane — that reaches the popup edge, without turning the popup into
// a scroll container, so a `scrollIntoView` inside still scrolls the panel and
// never the frame.
//
// Under a nested dialog the popup RECEDES rather than disappears: a 10% scale
// step and a 10% fade per open descendant (`--nested-dialogs`), with the
// child's own backdrop dimming it. Fading the parent out entirely reads as
// "the dialog closed and an alert opened", and the return on cancel as a
// reopen — the user loses the frame they were working in.
//
// The default FILL is `elevated`, not `popover`, on purpose: a dialog is a
// room, not a transient popup. Dark popovers lift past the card rung so a menu
// floats over whatever it opens on — spread across a dialog-sized region that
// bright fill reads washed, and a select opened INSIDE the dialog would sit at
// the dialog's own fill. The room stays a step off the page (its scrim and the
// popover-tier drop do the isolating; light resolves both fills to the same
// white, so this is a dark-mode statement). The `surface` variant swaps the
// fill only — the popover-tier drop shadow stays whatever the fill, because a
// modal layer over a scrim still has to read as the thing on top.
export const dialogPopupVariants = cva(
  "relative row-start-2 flex max-h-full min-h-0 w-full min-w-0 origin-center flex-col overflow-clip rounded-xl border not-dark:bg-clip-padding text-foreground opacity-[calc(1-0.1*var(--nested-dialogs))] shadow-popover outline-none transition-[scale,opacity,translate] duration-base ease-in-out will-change-transform before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-xl)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:opacity-0 data-starting-style:opacity-0 sm:scale-[calc(1-0.1*var(--nested-dialogs))] sm:data-ending-style:scale-98 sm:data-starting-style:scale-98 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
  {
    defaultVariants: {
      size: "lg",
      surface: "elevated",
    },
    variants: {
      /**
       * The fill the popup paints. `elevated` is the room. A quieter surface
       * is for a dialog that is the fullscreen presentation of a panel the
       * page also shows inline — a detail drawer grown to a modal — so the two
       * presentations paint the same fill and the flip between them reads as
       * the same page in a bigger frame, not a different kind of surface.
       */
      surface: {
        sunken: SURFACE_BG.sunken,
        background: SURFACE_BG.background,
        card: SURFACE_BG.card,
        elevated: SURFACE_BG.elevated,
        popover: SURFACE_BG.popover,
        sidebar: SURFACE_BG.sidebar,
      } satisfies Record<SurfaceToken, string>,
      /**
       * Popup width. `full` is the near-viewport dialog — a manager, a
       * lightbox, an admin console — and owns the ONE canonical gutter, so
       * those dialogs line up with each other instead of each picking its own
       * inset. Its width stays the base `w-full`: the viewport grid already
       * carries the gutter, so only the height and the width cap change.
       *
       * `window` is the tier between the content-sized caps and `full`: a
       * dialog that reads as a floating window over the page rather than a
       * takeover. Both axes are viewport-bounded like `full`, but scaled in —
       * a 3rem gutter each side and hard caps (70rem × 46rem) so on a large
       * monitor it holds a window's footprint instead of growing with the
       * screen. The height is fixed, not content-hugging: a window dialog is
       * a stable frame whose content pans inside it (a reference, a browser),
       * not a prompt that hugs its form.
       *
       * `screen` drops the gutter too: the whole viewport, for a phone viewer.
       */
      size: {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "4xl": "max-w-4xl",
        window:
          "h-[min(calc(100dvh-6rem),46rem)] max-w-[min(70rem,calc(100vw-6rem))]",
        full: "h-[calc(100dvh-4rem)] max-w-none",
        // Fixed to the viewport so the grid's gutter and the phone's top gap never reach it.
        screen:
          "fixed inset-0 h-dvh max-h-none max-w-none rounded-none border-0 before:hidden",
      },
      /**
       * Popup height ceiling. `content` (the default) hugs the content up to
       * the viewport grid's row. `capped` adds a hard cap below that (44rem)
       * for a dialog whose body is an open-ended list — a member roster, a
       * history, search results — so the popup settles at a frame height
       * and the list pans inside `DialogPanel` instead of the dialog
       * growing with the viewport until it is a takeover on a tall monitor.
       * The `window` and `full` sizes fix their own height; the cap is for
       * the content-sized widths.
       */
      height: {
        content: "",
        capped: "max-h-[min(100%,44rem)]",
      },
    },
  },
);

export type DialogSize = NonNullable<
  VariantProps<typeof dialogPopupVariants>["size"]
>;

export type DialogHeight = NonNullable<
  VariantProps<typeof dialogPopupVariants>["height"]
>;

export const DialogPortal: typeof DialogPrimitive.Portal =
  DialogPrimitive.Portal;

export function DialogTrigger(
  props: DialogPrimitive.Trigger.Props,
): React.ReactElement {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

export function DialogClose(
  props: DialogPrimitive.Close.Props,
): React.ReactElement {
  if (React.useContext(DialogSheetContext)) {
    return <DrawerClose {...(props as React.ComponentProps<typeof DrawerClose>)} />;
  }
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

export function DialogBackdrop({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props): React.ReactElement {
  // Base UI drops a nested dialog's backdrop on the assumption an ancestor
  // layer dimmed the page. Every modal layer paints its own scrim instead —
  // a dialog over a dialog dims the parent it stacks on, and one opened from
  // a non-modal, backdrop-less panel is not left over a bright page. See
  // AncestorLayerContext.
  return (
    <DialogPrimitive.Backdrop
      className={cn(
        "fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-all duration-base data-ending-style:opacity-0 data-starting-style:opacity-0",
        className,
      )}
      data-slot="dialog-backdrop"
      forceRender
      {...props}
    />
  );
}

export function DialogViewport({
  className,
  ...props
}: DialogPrimitive.Viewport.Props): React.ReactElement {
  // The viewport is `fixed inset-0`, sized against the LAYOUT viewport, which
  // iOS Safari (and any browser in `resizes-visual` mode) keeps full-height
  // while the software keyboard is up. The measured overlap becomes bottom
  // padding so a bottom-stuck popup rises above the keyboard; it is 0
  // wherever the layout viewport already shrinks (`resizes-content`).
  const keyboardInset = useVisualViewportBottomInset();
  return (
    <DialogPrimitive.Viewport
      className={cn(
        "fixed inset-0 z-50 grid grid-rows-[1fr_auto_3fr] justify-items-center p-4",
        className,
      )}
      data-slot="dialog-viewport"
      style={keyboardInset > 0 ? { paddingBottom: keyboardInset } : undefined}
      {...props}
    />
  );
}

/**
 * The portalled surface. It carries NO padding of its own — the gutter is
 * distributed: `DialogHeader` owns the top inset, `DialogFooter` the
 * bottom, and `DialogPanel` the body's inset and scroll. Body content
 * placed directly between header and footer renders flush against the
 * popup edge; ALWAYS wrap it in `DialogPanel`.
 */
export function DialogPopup({
  className,
  children,
  showCloseButton = true,
  bottomStickOnMobile = true,
  size,
  height,
  surface,
  closeProps,
  cornerActions,
  portalProps,
  initialFocus,
  ref,
  dialogOptions,
  ...props
}: DialogPrimitive.Popup.Props & Omittable<{
  /** The dialog's actions, built for the mode — see `DialogOptions`. */
  dialogOptions?: DialogOptions;
  size?: DialogSize;
  /** The height ceiling for a list-bodied dialog — see `dialogPopupVariants`. */
  height?: DialogHeight;
  /** The fill the popup paints — see `dialogPopupVariants`. */
  surface?: SurfaceToken;
  showCloseButton?: boolean;
  bottomStickOnMobile?: boolean;
  closeProps?: DialogPrimitive.Close.Props;
  /** Utility actions that share the close pill's corner — icon-only
   *  `icon-xs` neutral buttons (a copy-link, a help door), seated
   *  before the close so the close stays the last thing at the edge. For
   *  a dialog whose changes save as they are made and that therefore has
   *  no footer, this corner is where a secondary verb lives. */
  cornerActions?: React.ReactNode;
  portalProps?: DialogPrimitive.Portal.Props;
}>): React.ReactElement {
  // A coarse-pointer open lands on the popup, not on the first field inside
  // it (Base UI reads the click, which WebKit stamps "mouse" for a finger);
  // a field that claimed focus by its own `autoFocus` keeps it.
  const touchFocus = useTouchAwareInitialFocus<HTMLDivElement>(ref);
  // The sheet: the Drawer's own popup. `className` is left to the dialog —
  // it sizes and paints a window, which would fight the drawer's geometry —
  // and focus waits for `Dialog sheetFocus` once the sheet has settled.
  if (React.useContext(DialogSheetContext)) {
    return (
      <DrawerPopup
        {...(props as React.ComponentProps<typeof DrawerPopup>)}
        showBar
        surface={surface}
        cornerActions={cornerActions}
        ref={ref}
      >
        <DialogOptionsContext.Provider value={dialogOptions ?? null}>
          {children}
          {dialogOptions?.start != null ? (
            <DrawerFooter variant="bare">{dialogOptions.start}</DrawerFooter>
          ) : null}
        </DialogOptionsContext.Provider>
        {/* App-level dialogs opened from inside this sheet render here, so
            they stack on it. */}
        <DialogHost />
      </DrawerPopup>
    );
  }
  return (
    <DialogPortal {...portalProps}>
      <DialogBackdrop />
      {/* A modal layer: a drawer opened from inside paints its own scrim over
          this popup (only a drawer-on-drawer stack withholds one). */}
      <AncestorLayerContext.Provider value="modal">
        <DialogViewport
          className={cn(
            bottomStickOnMobile &&
              "max-sm:grid-rows-[1fr_auto] max-sm:p-0 max-sm:pt-12",
          )}
        >
          <DialogPrimitive.Popup
            className={cn(
              dialogPopupVariants({ height, size, surface }),
              bottomStickOnMobile &&
                // The bottom-stuck popup meets the screen edge, so it owes the
                // home-indicator safe area its own padding (viewport-fit=cover
                // consumers paint under it otherwise).
                "max-sm:max-w-none max-sm:origin-bottom max-sm:rounded-none max-sm:border-x-0 max-sm:border-t max-sm:border-b-0 max-sm:pb-[env(safe-area-inset-bottom,0px)] max-sm:data-ending-style:translate-y-4 max-sm:data-starting-style:translate-y-4 max-sm:before:hidden max-sm:before:rounded-none",
              className,
            )}
            data-slot="dialog-popup"
            initialFocus={initialFocus ?? touchFocus.initialFocus}
            ref={touchFocus.ref}
            {...props}
          >
            <DialogOptionsContext.Provider value={dialogOptions ?? null}>
              {children}
              {dialogOptions ? <DialogOptionsFooter options={dialogOptions} /> : null}
            </DialogOptionsContext.Provider>
            {(showCloseButton || cornerActions) && (
              <div
                className="absolute end-4 top-4 flex items-center gap-1"
                data-slot="dialog-corner"
              >
                {cornerActions}
                {showCloseButton && (
                  <DialogPrimitive.Close
                    aria-label="Close"
                    render={<Button size="icon-xs" color="neutral" />}
                    {...closeProps}
                  >
                    <Icon name="x" />
                  </DialogPrimitive.Close>
                )}
              </div>
            )}
          </DialogPrimitive.Popup>
        </DialogViewport>
      </AncestorLayerContext.Provider>
    </DialogPortal>
  );
}

export const dialogHeaderVariants = cva("flex", {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    variant: {
      default:
        "flex-col gap-2 p-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-panel])]:pb-3 max-sm:pb-4",
      /** Fullscreen workspaces use one compact identity/action bar instead of
       * the standard stacked title gutter. */
      toolbar: "relative h-12 shrink-0 flex-row items-center gap-2 px-4",
    },
  },
});

export function DialogHeader({
  className,
  variant,
  render,
  sheetActions,
  children,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  variant?: VariantProps<typeof dialogHeaderVariants>["variant"];
  /** Sheet only: further round `DialogSheetAction`s on the title row's
   *  trailing edge, before the primary. */
  sheetActions?: React.ReactNode;
}>): React.ReactElement {
  const sheet = React.useContext(DialogSheetContext);
  const options = React.useContext(DialogOptionsContext);
  const defaultProps = {
    className: cn(dialogHeaderVariants({ variant }), className),
    "data-slot": "dialog-header",
  };

  const header = useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, { ...props, children }),
    render,
  });
  if (!sheet) return header;
  // A sheet with declared actions wears the title row: × · title · word.
  // Without any, the header is the consumer's own (it may carry its own
  // close), so nothing is added.
  const titleRow = options != null || sheetActions != null;
  const cancel = options?.cancel ?? true;
  return (
    <DrawerHeader {...(props as React.ComponentProps<typeof DrawerHeader>)}>
      {titleRow ? (
        <DrawerTitleRow
          dismiss={typeof cancel === "object" ? cancel : true}
          primary={options?.primary}
          actions={sheetActions}
        >
          {children}
        </DrawerTitleRow>
      ) : (
        children
      )}
    </DrawerHeader>
  );
}

export const dialogFooterVariants = cva(
  [
    "flex flex-col-reverse gap-2 px-6 sm:flex-row sm:justify-end sm:rounded-b-[calc(var(--radius-xl)-1px)]",
    // Without a DialogPanel (which owns the body's bottom gutter), a bare
    // body Box would sit flush against the footer's border — the margin
    // restores the gap and is inert when a panel is present.
    "in-[[data-slot=dialog-popup]:not(:has([data-slot=dialog-panel]))]:mt-4",
  ],
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        default: "border-t py-4",
        bare: "in-[[data-slot=dialog-popup]:has([data-slot=dialog-panel])]:pt-3 pt-4 pb-6",
      },
    },
  },
);

export function DialogFooter({
  className,
  variant,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  variant?: VariantProps<typeof dialogFooterVariants>["variant"];
}>): React.ReactElement | null {
  const defaultProps = {
    className: cn(dialogFooterVariants({ variant }), className),
    "data-slot": "dialog-footer",
  };

  const footer = useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
  if (!React.useContext(DialogSheetContext)) return footer;
  return (
    <DrawerFooter
      {...(props as React.ComponentProps<typeof DrawerFooter>)}
      className={className}
      variant={variant === "bare" ? "bare" : "default"}
    />
  );
}

export function DialogTitle({
  className,
  visuallyHidden = false,
  ...props
}: DialogPrimitive.Title.Props &
  Omittable<{
    /** Names the dialog for assistive technology without painting: the
     *  visible header is something else (a breadcrumb, a search field). */
    visuallyHidden?: boolean;
  }>): React.ReactElement {
  if (React.useContext(DialogSheetContext)) {
    return (
      <DrawerTitle
        {...(props as React.ComponentProps<typeof DrawerTitle>)}
        className={className}
        visuallyHidden={visuallyHidden}
      />
    );
  }
  return (
    <DialogPrimitive.Title
      className={cn(
        "font-heading font-semibold text-xl leading-none",
        visuallyHidden && "sr-only",
        className,
      )}
      data-slot="dialog-title"
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  visuallyHidden = false,
  ...props
}: DialogPrimitive.Description.Props &
  Omittable<{
    /** Names the dialog for assistive technology without painting: the
     *  visible header is something else (a breadcrumb, a search field). */
    visuallyHidden?: boolean;
  }>): React.ReactElement {
  if (React.useContext(DialogSheetContext)) {
    return (
      <DrawerDescription
        {...(props as React.ComponentProps<typeof DrawerDescription>)}
        className={className}
        visuallyHidden={visuallyHidden}
      />
    );
  }
  return (
    <DialogPrimitive.Description
      className={cn(
        "text-muted-foreground text-sm",
        visuallyHidden && "sr-only",
        className,
      )}
      data-slot="dialog-description"
      {...props}
    />
  );
}

export const dialogPanelVariants = cva("", {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    variant: {
      default:
        "p-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-header])]:pt-1 in-[[data-slot=dialog-popup]:has([data-slot=dialog-footer]:not(.border-t))]:pb-1",
      /** Full-height workspaces own their internal scrolling and surface
       * layout. The panel supplies only the bounded flex region. */
      workspace: "flex min-h-0 flex-1 flex-col overflow-hidden p-0",
    },
  },
});

/**
 * The body slot — REQUIRED around any content between `DialogHeader` and
 * `DialogFooter`. The default variant owns the body's inset and scrolling.
 * `workspace` is a full-height bounded region whose children own both.
 */
export function DialogPanel({
  className,
  scrollFade = true,
  variant,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  scrollFade?: boolean;
  variant?: VariantProps<typeof dialogPanelVariants>["variant"];
}>): React.ReactElement {
  const defaultProps = {
    className: cn(dialogPanelVariants({ variant }), className),
    "data-slot": "dialog-panel",
  };

  const panel = useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });

  if (React.useContext(DialogSheetContext)) {
    return (
      <DrawerPanel
        {...(props as React.ComponentProps<typeof DrawerPanel>)}
        className={className}
        variant={variant === "workspace" ? "workspace" : undefined}
        scrollFade={scrollFade}
      />
    );
  }

  return variant === "workspace" ? panel : (
    <ScrollArea scrollFade={scrollFade}>{panel}</ScrollArea>
  );
}
